import { useTranslation } from 'react-i18next'
import { Check, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'

interface InlineEditBarProps {
  /** Number of edited cells. The bar slides away when there are none. */
  count: number
  /** Number of rows those cells belong to. */
  rowCount: number
  saving?: boolean
  onCancel: () => void
  onSave: () => void
}

/**
 * The save bar for edits made inside a list: the same sliding bar pinned to the
 * foot of the window that edit pages use. A list can live on a page that has a
 * save bar of its own, so this one stacks above it — list edits cover the
 * page's bar until they are saved or cancelled.
 */
export function InlineEditBar({ count, rowCount, saving, onCancel, onSave }: InlineEditBarProps) {
  const { t } = useTranslation()

  const visible = count > 0 || !!saving

  return (
    <div className={`detail-savebar z-20!${visible ? ' is-visible' : ''}`} aria-hidden={!visible}>
      <div className="detail-savebar__left">
        {saving ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            {t('common.saving')}
          </>
        ) : (
          count > 0 && (
            <>
              <span className="dot" />
              {t('inlineEdit.pendingChanges', { count, rows: rowCount })}
            </>
          )
        )}
      </div>
      <div className="detail-savebar__actions">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button type="button" onClick={onSave} loading={saving}>
          <Check className="size-4" />
          {t('common.save')}
        </Button>
      </div>
    </div>
  )
}
